// The text and icon editors' multi-selection helpers: which entries (a keycap's texts or
// icons) the settings panel edits, the value of each setting across them (common or MIXED),
// the colour a colour button shows for them, and the check on a tag's new name. TextEditor
// and IconEditor each held a copy, identical apart from reading `texts` or `icons`, until
// PR 22 of the test backfill moved them here.
//
// A selection is the design's `keycapData` plus `settings.selectedKeycaps` (positions as
// { rowIndex, keycapIndex }), the editor's selected entry key (`selectedTextIndex` or
// `selectedIconIndex`, the entry's key within the keycap's entries) and its `selectedTag`.

export const MIXED = Symbol('MIXED'); // marks a setting whose value differs across the target entries (one symbol for both editors, made once: a symbol made per render would never equal a stored one)

export function collectTargetEntries({
  keycapData,
  entriesKey, // 'texts' or 'icons'
  selectedKeycaps = [],
  selectedIndex = null,
  selectedTag = null,
  includeSingle = false, // whether a single selected entry (exactly one keycap and an entry key) counts; the editors edit it through its own values instead
  isoEnterOnly = false, // only the entries of ISO Enter keycaps, for the position.isoSection setting
}) {
  const targets = [];
  const skip = (kc) => !kc || kc.isSpacer || (isoEnterOnly && !kc.isIsoEnter); // a spacer (the tag walk crosses them), or a plain keycap when only the ISO Enter ones count; no selection points past a row (a layout switch resets the selection and a loaded design brings its own), so the missing-keycap check is a guard alone
  const eachEntry = (kc, fn) => {
    for (const entry of Object.values(kc[entriesKey] || {}))
      if (entry) fn(entry);
  };

  // 1. a tag selected: every entry referencing it, whatever the keycap selection
  if (selectedTag != null) {
    for (const row of keycapData) {
      for (const kc of row) {
        if (skip(kc)) continue;
        eachEntry(kc, (entry) => {
          if (entry.tagId === selectedTag) targets.push(entry);
        });
      }
    }
    return targets;
  }

  if (selectedKeycaps.length === 0) return targets;

  // 2. exactly one keycap and one of its entries selected: that entry, when asked for
  if (selectedKeycaps.length === 1 && selectedIndex != null) {
    if (!includeSingle) return targets;

    const { rowIndex, keycapIndex } = selectedKeycaps[0];
    const kc = keycapData[rowIndex]?.[keycapIndex];
    if (!skip(kc)) {
      const entry = (kc[entriesKey] || {})[String(selectedIndex)];
      if (entry) targets.push(entry);
    }

    return targets;
  }

  // 3. every entry of every selected keycap, in selection order
  for (const { rowIndex, keycapIndex } of selectedKeycaps) {
    const kc = keycapData[rowIndex]?.[keycapIndex];
    if (skip(kc)) continue;
    eachEntry(kc, (entry) => targets.push(entry));
  }

  return targets;
} // the entries the editor's settings apply to

export function commonValue(items, getter) {
  let firstSet = false;
  let first;

  for (const item of items) {
    const value = getter(item);

    if (!firstSet) {
      first = value; // the candidate every later item is compared with
      firstSet = true;
      continue;
    }

    if (!Object.is(value, first)) return MIXED; // Object.is rather than ===: NaN equals NaN, +0 and -0 differ
  }

  return firstSet ? first : undefined; // undefined for no items
} // the value the getter gives for every item, or MIXED when it differs

export function getByPath(obj, path, fallback) {
  let cur = obj;

  for (const part of path.split('.')) {
    if (cur == null) return fallback;
    cur = cur[part];
  }

  return cur == null ? fallback : cur;
} // the value at a dotted path ('style.fill'), or the fallback when the path or its value is missing (null counts as missing)

export function assignByPath(target, path, value) {
  const parts = path.split('.');
  let cur = target;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!cur[part]) cur[part] = {}; // the parent objects a new path needs
    cur = cur[part];
  }

  cur[parts[parts.length - 1]] = value;
} // sets the value at a dotted path, building the nested objects on the way

export const updateNestedField = (baseObject, updates) => {
  const updatedObject = { ...baseObject }; // Create a shallow copy of the base object

  Object.entries(updates).forEach(([subPropertyPath, value]) => {
    // Iterate over each update entry to modify the nested fields
    const subProperties = subPropertyPath.split('.'); // the path's parts
    let currentLevel = updatedObject;

    subProperties.forEach((subProperty, index) => {
      // Traverse the object according to the path to reach the target property
      if (index === subProperties.length - 1) {
        // Last property in the path, set its value
        currentLevel[subProperty] = value;
      } else {
        // Traverse deeper, or initialize an empty object if it doesn't exist
        currentLevel[subProperty] = currentLevel[subProperty] || {};
        currentLevel = currentLevel[subProperty];
      }
    });
  });
  return updatedObject;
}; // an entry with the updates written at their dotted paths ({ 'style.fill': '#ff0000ff' }), as the editors' handleKeycapChange applies every change. The top level is a new object; the nested objects along a path are written in place, which nothing observes: updateDesign deep-copies the design onto the undo stack before any update runs

export function computeCommonSettings({
  keycapData,
  entriesKey,
  selectedKeycaps,
  selectedIndex,
  selectedTag,
  fieldPaths, // every setting of an entry, as dotted paths
  defaultObject, // the editor's default entry, whose values stand in for a field an entry lacks
}) {
  const selection = {
    keycapData,
    entriesKey,
    selectedKeycaps,
    selectedIndex,
    selectedTag,
  };
  const targets = collectTargetEntries(selection); // empty for nothing selected, for a single selected entry, and for selected keycaps without entries
  if (targets.length === 0) return null;

  const valueOf = (entry, path) =>
    getByPath(entry, path, getByPath(defaultObject, path, undefined));
  const out = {};
  const ISO_PATH = 'position.isoSection';

  for (const path of fieldPaths) {
    if (path === ISO_PATH) continue; // from the ISO Enter keycaps alone, below

    assignByPath(
      out,
      path,
      commonValue(targets, (entry) => valueOf(entry, path))
    );
  }

  const isoTargets = collectTargetEntries({ ...selection, isoEnterOnly: true });
  assignByPath(
    out,
    ISO_PATH,
    isoTargets.length === 0
      ? undefined // no ISO Enter keycap among the targets' keycaps: neither a common value nor MIXED
      : commonValue(isoTargets, (entry) => valueOf(entry, ISO_PATH))
  );

  return out;
} // a nested object mirroring the entry's settings, each the common value across the target entries or MIXED; null when the settings panel edits no group of entries

export function resolveSettingValue({
  commonSettings, // computeCommonSettings' result
  selectedObject, // the selected entry, or the editor's default entry when none is selected
  path,
  defaultValue,
  mixedReturn = '', // what the control shows when the entries differ
}) {
  if (commonSettings) {
    const value = getByPath(commonSettings, path, undefined);

    if (value === MIXED) return mixedReturn;

    return value ?? defaultValue;
  }

  return getByPath(selectedObject, path, defaultValue);
} // the value a control shows for a setting

export function isMixed(commonSettings, path) {
  return (
    !!commonSettings && getByPath(commonSettings, path, undefined) === MIXED
  );
} // whether the target entries differ in a setting

export function pickMajorityColor(
  entries,
  path, // the colour setting, such as 'style.fill'
  defaultObject, // the editor's default entry, whose colour counts for an entry without one
  fallback // the colour for no entries
) {
  if (entries.length === 0) return fallback;

  const counts = new Map();
  const order = []; // the colours in the order they were first seen

  for (const entry of entries) {
    const color = getByPath(
      entry,
      path,
      getByPath(defaultObject, path, fallback)
    );

    if (!counts.has(color)) {
      counts.set(color, 0);
      order.push(color);
    }

    counts.set(color, counts.get(color) + 1);
  }

  const max = Math.max(...counts.values());

  return order.find((color) => counts.get(color) === max); // the most frequent colour, the first seen on a tie
} // the colour a colour button shows for the entries

export function uniformColorInfo(entries, settings, indexesKey) {
  const out = {};

  for (const setting of settings) {
    const indexes = entries.map(
      (entry) => entry[indexesKey]?.[setting] ?? null
    ); // which colour of the editor's list each entry references for the setting, null for none

    if (indexes.length === 0) {
      out[setting] = {
        hasSelection: false,
        isUniform: false,
        uniformIndex: null,
      };
      continue;
    }

    const first = indexes[0];
    const isUniform = indexes.every((index) => index === first);
    out[setting] = {
      hasSelection: true, // there are entries (the colour list then shows which of its colours they reference)
      isUniform, // every entry references the same colour, or none does
      uniformIndex: isUniform ? first : null,
    };
  }

  return out;
} // per colour setting, whether the entries reference the same colour of the editor's colour list

export function validateTagRename(prevTags, index, rawName) {
  const trimmed = (rawName ?? '').trim();

  if (!trimmed) return { ok: false, reason: 'empty' };

  if (trimmed.length > 100) return { ok: false, reason: 'too-long' };

  const conflict = prevTags.some(
    (tag, i) =>
      i !== index && tag.name?.trim().toLowerCase() === trimmed.toLowerCase()
  );
  if (conflict) return { ok: false, reason: 'already-in-use' };

  return { ok: true, name: trimmed };
} // the checks on a new name for the tag at index: not empty once trimmed, at most 100 characters, not another tag's name in any case; the editor turns the reason into its toast
